import { useState } from 'react';
import { Box, Button, ButtonLink, Flex, Text } from '@backstage/ui';
import { NeedsYouItem, NeedsYouUrgency } from './types';
import './needsYou.css';

/** Rows shown before "Show N more". */
export const COLLAPSED_COUNT = 6;

const URGENCY_COLOR: Record<NeedsYouUrgency, string> = {
  critical: 'var(--bui-fg-danger)',
  warning: 'var(--bui-fg-warning)',
  normal: 'var(--make-fg-brand)',
  low: 'var(--bui-fg-secondary)',
};

const NeedsYouRow = ({
  item,
  onAction,
}: {
  item: NeedsYouItem;
  onAction: (item: NeedsYouItem) => void;
}) => (
  <li className="needs-you-row">
    <Flex align="center" gap="3" py="3" px="5">
      <span
        aria-hidden="true"
        style={{
          alignSelf: 'stretch',
          width: 3,
          flexShrink: 0,
          borderRadius: 'var(--bui-radius-full)',
          backgroundColor:
            item.urgency === 'low'
              ? 'var(--bui-border-2)'
              : URGENCY_COLOR[item.urgency],
        }}
      />
      <Box style={{ flex: 1, minWidth: 0 }}>
        {/* Colour stays on the bar: the label reads in neutral text, so it
        keeps its contrast at this size in both themes. */}
        <Text as="p" variant="body-small">
          <Text
            as="span"
            variant="body-small"
            weight="bold"
            style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
          >
            {item.label}
          </Text>
          {item.meta && (
            <Text as="span" variant="body-small" color="secondary">
              {' · '}
              {item.meta}
            </Text>
          )}
        </Text>
        <Text as="p" variant="body-medium" weight="bold">
          {item.title}
        </Text>
        {item.detail && (
          <Text as="p" variant="body-small" color="secondary" truncate>
            {item.detail}
          </Text>
        )}
      </Box>
      <ButtonLink
        size="small"
        // One quiet button style: how urgent a row is shows in its bar and
        // its label, and a filled brand button read as "click here first".
        variant="secondary"
        href={item.action.href}
        target={item.action.external ? '_blank' : undefined}
        rel={item.action.external ? 'noopener noreferrer' : undefined}
        aria-label={`${item.action.label}: ${item.title}`}
        onPress={() => onAction(item)}
      >
        {item.action.label}
      </ButtonLink>
    </Flex>
  </li>
);

/**
 * Rows that run edge to edge across the card, each with a coloured bar, a
 * category, short facts and one action. The first COLLAPSED_COUNT show, the
 * rest behind "Show N more". Shared by both tabs of the "Your work" card.
 */
export const NeedsYouList = ({
  label,
  items,
  onAction,
}: {
  label: string;
  items: NeedsYouItem[];
  onAction: (item: NeedsYouItem) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, COLLAPSED_COUNT);
  return (
    <>
      <Box
        as="ul"
        aria-label={label}
        style={{
          listStyle: 'none',
          // Rows run edge to edge across the card.
          margin: '0 calc(-1 * var(--bui-space-5))',
          borderTop: '1px solid var(--bui-border-1)',
          padding: 0,
        }}
      >
        {visible.map(item => (
          <NeedsYouRow key={item.id} item={item} onAction={onAction} />
        ))}
      </Box>
      {items.length > COLLAPSED_COUNT && (
        <Button
          size="small"
          variant="tertiary"
          onPress={() => setExpanded(!expanded)}
        >
          {expanded
            ? 'Show fewer'
            : `Show ${items.length - COLLAPSED_COUNT} more`}
        </Button>
      )}
    </>
  );
};
